import { Trans } from '@lingui/macro';
import { ArrowLeft, X, Loader2, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Favorite } from '../../models/Favorite';
import { Message } from '../../models/Message';
import { Room } from '../../models/Room';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import MessageItem from '../components/MessageItem';
import { UIMessage } from '../components/UIMessage';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';

var ALL_ROOMS = 'all-rooms';

const FavoritesPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string; }>();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const { rooms } = useAppUIContext();
  const [room, setRoom] = useState<Room>();
  const [favs, setFavs] = useState<Favorite[]>([]);

  const roomName = room ? Room.getRoomNameWithSharp(room, My.user.id) : '';

  useEffect(() => {
    init(roomId || "");
  }, [roomId]);

  async function init(roomId: string) {
    setRoom(rooms.find(r => r.id === roomId));
    setInProgress(true);
    setFavs(await Fire.msg.getFavorites(My.user.id, roomId == ALL_ROOMS ? null : roomId));
    setInProgress(false);
  }

  function goToRoom() {
    if (roomId == ALL_ROOMS)
      history.goBack();
    else
      history.replace(`/room/${roomId}`);
  }

  function showAllFavs() {
    history.push(`/room/${ALL_ROOMS}/favorites`);
  }

  return (
    <div className='flex flex-col h-full bg-[#f0f2f5] md:rounded-panel overflow-hidden'>
      <header className='flex items-center px-4 py-3 bg-white border-b border-gray-200 min-h-[56px] shrink-0'>
        <Button variant="ghost" size="icon" onClick={goToRoom} className="-ml-2 mr-2">
          {roomName ? <X className="w-6 h-6" /> : <ArrowLeft className="w-6 h-6" />}
        </Button>
        <h1 className='text-lg font-semibold flex-1 truncate pr-2'>
          {roomName ? roomName : <Trans>All my saved messages</Trans>}
        </h1>
        {inProgress && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
      </header>

      <main className='flex-1 overflow-y-auto p-4 md:p-6'>
        <div className='max-w-3xl mx-auto'>
          {roomName && (
            <div className='flex items-center justify-between mb-4 px-1'>
              <h2 className='text-sm font-bold text-gray-500 uppercase tracking-wide'><Trans>My saved messages</Trans></h2>
              <Button variant="outline" size="sm" onClick={showAllFavs} className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-200 h-8 text-xs rounded-full">
                <Trans>All my saved messages</Trans>
              </Button>
            </div>
          )}

          <div className="space-y-4">
            {favs.length === 0 && !inProgress && (
              <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500 bg-white rounded-lg border border-dashed border-gray-300 p-8">
                <p className="text-sm"><Trans>You have not saved any messages from {roomName}.</Trans></p>
              </div>
            )}

            {favs.map((f) => (
              <div key={f.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <FavoriteItem fav={f} showRoom={roomId == ALL_ROOMS} />
              </div>
            ))}
          </div>
        </div>
      </main>
    </div >
  );
}

const FavoriteItem: React.FC<{ fav: Favorite, showRoom: boolean }> = ({ fav, showRoom }) => {
  const [m] = useState<UIMessage>(new UIMessage(fav.msg, []));
  const history = useHistory();
  // const [inProgress, setInProgress] = useState(false); // Unused in original code effectively except for non-existent implementation

  function getRoomName() {
    const room = My.Rooms.get(m.msg.roomId);
    if (room) {
      return Room.getRoomNameWithSharp(room, My.user.id);
    } else {
      return m.msg.roomId;
    }
  }

  function emptyUIMessage() {
    return new UIMessage(new Message(new Room("", ""), My.user), []);
  }

  return (
    <>
      <div className="flex flex-col">
        {showRoom && (
          <div
            className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => history.push(`/room/${m.msg.roomId}`)}
          >
            <span className="font-semibold text-sm text-gray-700">{getRoomName()}</span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        )}

        <div className="p-2 message-box">
          <MessageItem
            m={m}
            state='favorite'
            onMessageClick={() => { }}
            parent={m.msg.parentMessageId ? emptyUIMessage() : undefined}
            isViewButton={true}
          />
        </div>

        {m.nonReactions().length > 0 && (
          <div className="bg-gray-50 border-t border-gray-100 p-2 pl-6">
            {m.nonReactions().map(c => (
              <div key={c.msg.id} className="mt-2 first:mt-0">
                <MessageItem m={c} state='favorite' onMessageClick={() => { }} parent={m} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default FavoritesPage;
